// «Сообщения» — conversaţiile şi ce s-a întâmplat cu ce ai scris.
//
// Două lucruri diferite care până acum erau amestecate: mesajele private stăteau într-o
// fereastră care se deschidea de oriunde, iar abonările şi răspunsurile la publicaţiile
// tale — într-un panou de sub clopoţel, lângă alertele de preţ. Niciunul n-avea un loc
// unde să te întorci.
//
// Acum au unul, cu două file, fiindcă răspunsul la fiecare e diferit: la o conversaţie
// scrii înapoi, la un abonat te uiţi cine e. Un contor comun n-ar spune în care din ele
// să te uiţi, deci fiecare filă îşi poartă cifra ei.
//
// Aşezarea diferă între aparate şi asta e voit: pe ecran lat, lista de conversaţii la
// stânga şi discuţia la dreapta, ca într-un client de poştă; pe telefon, lista întâi,
// discuţia peste ea, cu un buton de întoarcere.

import { el, clear, bus } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { session } from '../core/session.js';
import { pictAvatar } from '../ui/avatars.js';
import { toast } from '../ui/components.js';
import { esteMobil } from '../core/device.js';

const json = async (path, body, metoda) => {
  const resp = await fetch(path, body || metoda
    ? { method: metoda || 'POST', headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined }
    : {});
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.detail || `Ошибка ${resp.status}`);
  return data;
};

/** Cât de des recitim conversaţia deschisă. */
const REIMPROSPATARE = 8000;

function cand(ts) {
  const s = Math.floor(Date.now() / 1000) - ts;
  if (s < 60) return 'сейчас';
  if (s < 3600) return `${Math.floor(s / 60)} мин`;
  if (s < 86400) return `${Math.floor(s / 3600)} ч`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} д`;
  return new Date(ts * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/** Ce scrie pe rândul unei înştiinţări. Verdictul n-are actor: îl dă preţul. */
function textInstiintare(n) {
  const cine = n.actor?.username || '';
  switch (n.kind) {
    case 'comment': return `${cine} ответил на вашу публикацию`;
    case 'reply': return `${cine} ответил на ваш комментарий`;
    case 'mention': return `${cine} упомянул вас`;
    case 'reaction': return `${cine} оценил вашу публикацию`;
    case 'follow': return `${cine} подписался на вас`;
    case 'verdict': return `Вердикт по вашей идее: ${n.text}`;
    default: return n.text || '';
  }
}

const ICOANE = {
  comment: 'comments', reply: 'comments', mention: 'user',
  reaction: 'heartFull', follow: 'plus', verdict: 'shieldCheck',
};

export class MessagesSection {
  constructor(app) {
    this.app = app;
    this.fila = new URLSearchParams(location.search).get('tab') === 'alerts' ? 'mine' : 'chats';
    this.cuCine = new URLSearchParams(location.search).get('to') || null;
    this.mobil = esteMobil();
  }

  async mount(host) {
    this.root = el('div', { class: `msg-section${this.mobil ? ' mobil' : ''}` });
    host.append(this.root);
    this.#construieste();
    this.offSession = bus.on('session:change', () => this.#incarca());
    await this.#incarca();
  }

  #construieste() {
    clear(this.root);
    this.insignaChats = el('span', { class: 'msg-insigna' });
    this.insignaMine = el('span', { class: 'msg-insigna' });

    this.filaChatsEl = el('button', {
      class: 'msg-fila', onclick: () => this.#schimba('chats'),
    }, [icon('send', 13), el('span', { text: 'Переписки' }), this.insignaChats]);
    this.filaMineEl = el('button', {
      class: 'msg-fila', onclick: () => this.#schimba('mine'),
    }, [icon('bell', 13), el('span', { text: 'Уведомления' }), this.insignaMine]);

    this.listaEl = el('div', { class: 'msg-lista' });
    this.discutieEl = el('div', { class: 'msg-discutie' });

    this.root.append(el('div', { class: 'msg-doc' }, [
      el('div', { class: 'msg-file' }, [this.filaChatsEl, this.filaMineEl]),
      el('div', { class: 'msg-corp' }, [this.listaEl, this.discutieEl]),
    ]));
    this.#syncFile();
  }

  #syncFile() {
    this.filaChatsEl.classList.toggle('activa', this.fila === 'chats');
    this.filaMineEl.classList.toggle('activa', this.fila === 'mine');
    // Pe telefon, discuţia deschisă acoperă lista; pe ecran lat stau alături.
    this.root.classList.toggle('cu-discutie', !!this.cuCine);
    // Fila de înştiinţări n-are a doua coloană, deci lista ia toată lăţimea. Fără asta
    // rândurile stăteau strânse în cei 320 de pixeli ai listei de conversaţii, iar
    // textul se tăia la jumătate de cuvânt lângă un ecran gol.
    this.root.classList.toggle('doar-lista', this.fila === 'mine');
  }

  #schimba(fila) {
    if (this.fila === fila) return;
    this.fila = fila;
    this.cuCine = null;
    history.replaceState({}, '', fila === 'mine' ? '/messages/?tab=alerts' : '/messages/');
    this.#syncFile();
    this.#incarca();
  }

  async #incarca() {
    clear(this.discutieEl);
    clear(this.listaEl).append(el('div', { class: 'fd-gol-mic', text: 'Загружаем…' }));
    try {
      if (this.fila === 'chats') await this.#incarcaConversatii();
      else await this.#incarcaInstiintari();
    } catch (err) {
      clear(this.listaEl).append(el('div', { class: 'fd-gol', text: err.message }));
    }
    this.#cifre();
  }

  /** Cifrele de pe file. Se cer separat, ca să fie corecte şi când fila nu e deschisă. */
  async #cifre() {
    try {
      const d = await json('/api/social/notifications', null, 'GET');
      const pe = d.pe_feluri || {};
      for (const [insigna, n] of [[this.insignaChats, pe.mesaje], [this.insignaMine, pe.despre_mine]]) {
        insigna.textContent = n ? String(n) : '';
        insigna.classList.toggle('are', !!n);
      }
      // Fila deschisă se consideră citită — dar numai ea.
      await json(`/api/social/notifications/read?kind=${this.fila === 'chats' ? 'dm' : 'mine'}`,
        null, 'POST');
    } catch { /* cifrele sunt un plus, nu un motiv de eroare pe ecran */ }
  }

  // ------------------------------------------------------------ conversaţii

  async #incarcaConversatii() {
    const d = await json('/api/dm', null, 'GET');
    const fire = d.threads || [];
    clear(this.listaEl);

    if (!fire.length) {
      this.listaEl.append(el('div', { class: 'fd-gol' }, [
        el('div', { text: 'Переписок пока нет' }),
        el('div', { class: 'ideas-empty-sub',
          text: 'Откройте профиль участника и нажмите «Написать».' }),
      ]));
      return;
    }

    for (const t of fire) {
      const av = el('span', { class: 'fd-av' });
      pictAvatar(av, t.user, 40);
      this.listaEl.append(el('button', {
        class: `msg-rand${this.cuCine === t.user.username ? ' pe' : ''}`,
        onclick: () => this.deschideDiscutia(t.user.username),
      }, [
        av,
        el('div', { class: 'msg-rand-corp' }, [
          el('div', { class: 'msg-rand-cap' }, [
            el('span', { class: 'msg-nume', text: t.user.username }),
            el('span', { class: 'fd-cand', text: cand(t.at) }),
          ]),
          el('span', { class: 'msg-ultim', text: t.last || '' }),
        ]),
        t.unread ? el('span', { class: 'msg-necitit', text: String(t.unread) }) : null,
      ]));
    }

    // Pe ecran lat deschidem prima conversaţie: un panou gol lângă o listă plină arată
    // ca o pagină care nu s-a încărcat.
    if (!this.mobil && !this.cuCine && fire.length) this.deschideDiscutia(fire[0].user.username);
    else if (this.cuCine) this.deschideDiscutia(this.cuCine);
  }

  /** Conversaţia cu cineva, în panoul din dreapta (sau peste listă, pe telefon). */
  async deschideDiscutia(nume) {
    this.cuCine = nume;
    this.#syncFile();
    for (const b of this.listaEl.querySelectorAll('.msg-rand')) {
      b.classList.toggle('pe', b.textContent.startsWith(nume));
    }

    const lista = el('div', { class: 'dm-lista' });
    const camp = el('input', { class: 'fd-com-input', type: 'text', maxlength: 800,
      placeholder: 'Сообщение…' });
    const capAvatar = el('span', { class: 'fd-av mic' });

    const deseneaza = (d) => {
      const eraJos = lista.scrollTop + lista.clientHeight >= lista.scrollHeight - 30;
      clear(lista);
      if (d.user) pictAvatar(capAvatar, d.user, 30);
      if (!d.messages.length) {
        lista.append(el('div', { class: 'fd-gol-mic', text: 'Переписки ещё нет' }));
      }
      for (const m of d.messages) {
        lista.append(el('div', { class: `dm-msg${m.mine ? ' al-meu' : ''}` }, [
          m.shot ? el('img', {
            class: 'dm-poza', src: m.shot, alt: '', loading: 'lazy',
            onclick: async () => {
              const { deschidePoza } = await import('../ui/lightbox.js');
              deschidePoza(m.shot);
            },
          }) : null,
          m.text ? el('span', { class: 'dm-text', text: m.text }) : null,
          el('span', { class: 'dm-cand', text: cand(m.created_at) }),
        ]));
      }
      if (eraJos) lista.scrollTop = lista.scrollHeight;
    };

    // Captura luata de pe grafic, daca asteapta una. Apare doar cand exista ce trimite.
    let capturaDeTrimis = '';
    const chip = el('button', { class: 'fd-captura', type: 'button' });
    chip.style.display = 'none';

    const syncChip = async () => {
      const { iaCaptura } = await import('../ui/captura.js');
      const c = iaCaptura();
      if (!c) { chip.style.display = 'none'; capturaDeTrimis = ''; return; }
      chip.style.display = '';
      clear(chip).append(
        el('img', { class: 'fd-captura-mic', src: c.url, alt: '' }),
        el('span', { text: capturaDeTrimis ? 'Снимок готов к отправке'
          : (c.eticheta ? `Прикрепить снимок ${c.eticheta}` : 'Прикрепить снимок графика') }),
        icon(capturaDeTrimis ? 'check' : 'plus', 11),
      );
      chip.classList.toggle('ales', !!capturaDeTrimis);
    };
    chip.onclick = async () => {
      const { iaCaptura } = await import('../ui/captura.js');
      capturaDeTrimis = capturaDeTrimis ? '' : (iaCaptura()?.url || '');
      syncChip();
    };
    syncChip();

    const trimite = async () => {
      const text = camp.value.trim();
      if (!text && !capturaDeTrimis) return;
      camp.value = '';
      const poza = capturaDeTrimis;
      capturaDeTrimis = '';
      try {
        deseneaza(await json(`/api/dm/${encodeURIComponent(nume)}`, { text, shot: poza }));
        if (poza) {
          const { goleste } = await import('../ui/captura.js');
          goleste();
        }
        syncChip();
      } catch (err) { toast(err.message, 'error'); }
    };
    camp.onkeydown = (e) => { if (e.key === 'Enter') trimite(); };

    clear(this.discutieEl).append(
      el('header', { class: 'msg-cap' }, [
        this.mobil ? el('button', { class: 'mt-btn', title: 'Назад',
          onclick: () => { this.cuCine = null; this.#syncFile(); } }, [icon('chevronLeft', 16)]) : null,
        capAvatar,
        el('button', { class: 'msg-cap-nume', text: nume,
          onclick: () => this.app.navigate(`/profile/${encodeURIComponent(nume)}/`) }),
      ]),
      lista,
      el('div', { class: 'dm-jos' }, [
        chip,
        el('div', { class: 'dm-scrie' }, [
          camp, el('button', { class: 'fd-com-send', onclick: trimite }, [icon('send', 12)]),
        ]),
      ]),
    );

    clearInterval(this.ceas);
    const reincarca = async () => {
      try { deseneaza(await json(`/api/dm/${encodeURIComponent(nume)}`, null, 'GET')); }
      catch { /* o pierdere de reţea nu are de ce să golească fereastra */ }
    };
    this.ceas = setInterval(reincarca, REIMPROSPATARE);
    await reincarca();
    setTimeout(() => camp.focus(), 60);
  }

  // ----------------------------------------------------------- înştiinţări

  async #incarcaInstiintari() {
    const d = await json('/api/social/notifications', null, 'GET');
    // Mesajele private au fila lor; aici rămâne doar ce s-a întâmplat cu ce ai scris.
    const lista = (d.items || []).filter((n) => n.kind !== 'dm');
    clear(this.listaEl);
    clear(this.discutieEl);

    if (!lista.length) {
      this.listaEl.append(el('div', { class: 'fd-gol' }, [
        el('div', { text: 'Пока тихо' }),
        el('div', { class: 'ideas-empty-sub',
          text: 'Здесь появятся ответы на ваши публикации, упоминания, '
            + 'подписки и вердикты по вашим идеям.' }),
      ]));
      return;
    }

    for (const n of lista) {
      const av = el('span', { class: 'fd-av mic' });
      if (n.actor) pictAvatar(av, n.actor, 32);
      else av.append(icon('shieldCheck', 14));

      this.listaEl.append(el('button', {
        class: `msg-rand${n.read ? '' : ' nou'}`,
        onclick: () => this.#deschideInstiintarea(n),
      }, [
        av,
        el('div', { class: 'msg-rand-corp' }, [
          el('div', { class: 'msg-rand-cap' }, [
            el('span', { class: 'msg-nume', text: textInstiintare(n) }),
            el('span', { class: 'fd-cand', text: cand(n.created_at) }),
          ]),
          n.post_text ? el('span', { class: 'msg-ultim', text: n.post_text }) : null,
        ]),
        el('span', { class: 'msg-ico' }, [icon(ICOANE[n.kind] || 'bell', 12)]),
      ]));
    }
  }

  #deschideInstiintarea(n) {
    if (n.kind === 'follow' && n.actor?.username) {
      this.app.navigate(`/profile/${encodeURIComponent(n.actor.username)}/`);
      return;
    }
    const eu = session.user?.username;
    if (eu) this.app.navigate(`/feed/?author=${encodeURIComponent(eu)}`);
  }

  unmount() {
    clearInterval(this.ceas);
    this.offSession?.();
    this.root?.remove();
  }
}
